// JOURNAL D'AUTHENTIFICATION — CCT-138.
//
// But : savoir QUAND et POURQUOI une session casse (jeton renouvelé ? remplacé par une autre
// session ? révoqué ?), en corrélant les sessions ouvertes en parallèle (app à la main +
// harnais, comptes alias de la même boîte).
//
// ⚠️ On ne journalise JAMAIS la valeur d'un jeton. Un access/refresh token écrit dans une
// console ou un fichier est une CREDENTIAL en clair : quiconque lit le journal peut rejouer la
// session. On enregistre :
//   • une EMPREINTE courte et non réversible (permet de dire « même jeton » / « jeton changé ») ;
//   • les claims NON secrets utiles au diagnostic : `sid` (session Entra), `iat`, `exp`, `sub`.
// Cela répond exactement à la question posée — le contenu du jeton, lui, n'apporterait rien.

export type AuthEvent =
  | 'session'        // une session est appliquée (login, restauration, renouvellement)
  | 'refresh-ok'
  | 'refresh-ko'
  | 'http-401'
  | 'logout';

export interface AuthTraceEntry {
  at: string;             // ISO
  event: AuthEvent;
  fp?: string;            // empreinte du jeton (8 hex) — jamais le jeton
  sid?: string;           // identifiant de session côté fournisseur
  iat?: number;
  exp?: number;
  expIn?: number;         // secondes restantes au moment de l'événement
  sub?: string;
  detail?: string;        // ex. chemin de l'appel qui a pris le 401
}

const MAX = 100;                       // anneau : un onglet ouvert des heures ne doit pas gonfler

// ⚠️ THE JOURNAL DID NOT SURVIVE THE EVENT IT EXISTS TO EXPLAIN. It lived only in memory: a
// sign-out that reloads the page took away exactly the lines naming its cause, and the incident —
// recurrent, and usually happening while nobody is watching — stayed undiagnosable. So it is
// PERSISTED.
//
// ⚠️ STILL NO TOKEN IS EVER WRITTEN. What is stored is what was already logged: a non-reversible
// fingerprint and non-secret claims. Persisting a token VALUE would turn this into a plaintext
// credential, readable by any browser extension.
const CLE = 'cc.authTrace';

function readFromStorage(): AuthTraceEntry[] {
  try {
    const brut = typeof window !== 'undefined' ? window.localStorage.getItem(CLE) : null;
    const out = brut ? JSON.parse(brut) : [];
    return Array.isArray(out) ? out.slice(-MAX) : [];
  } catch {
    return [];                          // storage unavailable: the journal stays in memory
  }
}

const entries: AuthTraceEntry[] = readFromStorage();

/** Empreinte NON RÉVERSIBLE et courte (FNV-1a 32 bits). Suffit à comparer deux jetons ;
 *  ne permet pas de reconstruire le jeton. */
function fingerprint(token?: string | null): string | undefined {
  if (!token) return undefined;
  let h = 0x811c9dc5;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Claims NON secrets d'un JWT (aucune vérification de signature : usage diagnostic). */
function claims(token?: string | null): Partial<AuthTraceEntry> {
  if (!token || token.split('.').length < 2) return {};
  try {
    const p = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const now = Math.floor(Date.now() / 1000);
    return { sid: p.sid, iat: p.iat, exp: p.exp, sub: p.sub, expIn: p.exp ? p.exp - now : undefined };
  } catch { return {}; }
}

//: The events that ESCALATE to telemetry. Deliberately the only two that explain a sign-out.
//:
//: ⚠️ SUCCESSES ARE NOT SENT. `session` and `refresh-ok` fire on every renewal, roughly once an
//: hour per open tab: permanent volume for something nobody ever reads back. Failures are rare,
//: and they are the only ones anyone comes looking for. This project runs on the owner's own
//: money; useful telemetry is telemetry that only bills for what will be read.
const REMONTES: AuthEvent[] = ['refresh-ko', 'logout'];

/**
 * Escalates an authentication failure all the way to the server telemetry.
 *
 * ⚠️ WITHOUT THIS, THE CAUSE STAYS IN THE PLAYER'S BROWSER. The journal above lives in the
 * console and in `localStorage`, so diagnosing means asking the person who was signed out to copy
 * a line, which assumes they had the developer tools open at the right moment. A failure that can
 * only be diagnosed by disturbing its victim is not diagnosed.
 *
 * The channel already exists (`POST /insights/events`): buffered, PERSISTED and replayed on the
 * next load, which is exactly what is needed here since a sign-out RELOADS the page. The backend
 * re-emits it as a `client.auth` span, correlatable by session.
 *
 * ⚠️ NO TOKEN EVER LEAVES, as everywhere in this file: the non-reversible fingerprint and the
 * non-secret claims, nothing else.
 */
function remonter(e: AuthTraceEntry): void {
  try {
    // LAZY import: the journal must depend on nothing (see the header), and observability never
    // breaks an authentication.
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const beacon = require('../insights/beacon');
    beacon.track({
      type: 'auth',
      action: e.event,
      meta: { fp: e.fp, sid: e.sid, expIn: e.expIn, detail: e.detail },
    });
    // ⚠️ THE BUFFER IS FLUSHED AT ONCE ON A SIGN-OUT. It switches navigator and reloads: waiting
    // for the periodic send would push the event into the NEXT session, or lose it entirely if
    // the tab closes.
    if (e.event === 'logout') beacon.flush();
  } catch {
    /* telemetry never breaks authentication */
  }
}

export function traceAuth(event: AuthEvent, token?: string | null, detail?: string): void {
  const e: AuthTraceEntry = { at: new Date().toISOString(), event, fp: fingerprint(token), ...claims(token) };
  if (detail) e.detail = detail;
  entries.push(e);
  if (entries.length > MAX) entries.shift();
  // Visible en console ET exposé pour le harnais : la déconnexion se produit souvent quand
  // personne ne regarde, il faut pouvoir relire l'historique après coup.
  console.info('[auth]', event, e.fp ?? '-', e.sid ? `sid=${e.sid}` : '',
    e.expIn !== undefined ? `exp dans ${e.expIn}s` : '', detail ?? '');
  if (REMONTES.includes(event)) remonter(e);
  if (typeof window !== 'undefined') {
    (window as any).__ccAuthTrace = entries;
    try {
      window.localStorage.setItem(CLE, JSON.stringify(entries));
    } catch { /* quota or private mode: memory remains; never break authentication for a log */ }
  }
}

/** Clears the journal. Explicit on purpose: signing out must NOT erase it — reading it is
 *  precisely what one comes to do afterwards. */
export function clearAuthTrace(): void {
  entries.length = 0;
  try { window.localStorage.removeItem(CLE); } catch { /* nothing to do */ }
}

export function getAuthTrace(): AuthTraceEntry[] { return entries; }
